import mockCategories from '../mockData/mockCategories';
import { apiClient } from './apiClient';

const API_PATH = '/categories';

class CategoriesService {
  constructor() {
    // Kopie der Mock-Daten für lokale Änderungen
    this.categories = [...mockCategories];
    this.nextId = Math.max(...mockCategories.map(c => c.id)) + 1;
  }

  // Normalisiert Backend-Daten in das erwartete Frontend-Format
  normalizeCategory(category) {
    if (!category) return null;
    
    return {
      id: category.id,
      name: category.name,
      type: category.type,
      ageGroups: category.ageGroups ? category.ageGroups.map(ag => ag.name) : [],
      ageGroupIds: category.ageGroups ? category.ageGroups.map(ag => ag.id) : [],
      createdAt: category.createdAt,
      updatedAt: category.updatedAt
    };
  }

  // Normalisiert ein Array von Kategorien
  normalizeCategories(categories) {
    return categories.map(cat => this.normalizeCategory(cat));
  }

  // Simuliert API-Verzögerung
  async delay(ms = 200) {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  // Alle Kategorien abrufen
  async getAllCategories() {
    try {
      const data = await apiClient.get(API_PATH);
      const normalizedData = this.normalizeCategories(data);
      return { success: true, data: normalizedData, total: normalizedData.length };
    } catch (e) {
      console.warn('API-Fehler, verwende Mock-Daten:', e.message);
      return { success: true, data: this.categories, total: this.categories.length, fallback: true };
    }
  }

  // Kategorie nach ID abrufen
  async getCategoryById(id) {
    try {
      const data = await apiClient.get(`${API_PATH}/${id}`);
      const normalizedData = this.normalizeCategory(data);
      return { success: true, data: normalizedData };
    } catch (e) {
      const category = this.categories.find(c => c.id === id);
      if (!category) return { success: false, error: 'Kategorie nicht gefunden', data: null, fallback: true };
      return { success: true, data: category, fallback: true };
    }
  }

  // Neue Kategorie erstellen
  async createCategory(categoryData) {
    try {
      const payload = {
        name: categoryData.name,
        type: categoryData.type,
        ageGroupIds: categoryData.ageGroupIds || categoryData.ageGroups
      };
      const data = await apiClient.post(API_PATH, payload);
      const normalizedData = this.normalizeCategory(data);
      return { success: true, data: normalizedData, message: 'Kategorie erfolgreich erstellt' };
    } catch (e) {
      return { success: false, error: e.message, fallback: true };
    }
  }

  // Kategorie aktualisieren
  async updateCategory(id, categoryData) {
    try {
      const payload = {
        name: categoryData.name,
        type: categoryData.type,
        ageGroupIds: categoryData.ageGroupIds || categoryData.ageGroups
      };
      const data = await apiClient.put(`${API_PATH}/${id}`, payload);
      const normalizedData = this.normalizeCategory(data);
      return { success: true, data: normalizedData, message: 'Kategorie erfolgreich aktualisiert' };
    } catch (e) {
      return { success: false, error: e.message, fallback: true };
    }
  }

  // Kategorie löschen
  async deleteCategory(id) {
    try {
      await apiClient.delete(`${API_PATH}/${id}`);
      return { success: true, message: 'Kategorie erfolgreich gelöscht' };
    } catch (e) {
      return { success: false, error: e.message, fallback: true };
    }
  }

  // Kategorien nach Typ abrufen (verwendet lokale Filterung)
  async getCategoriesByType(type) {
    try {
      const result = await this.getAllCategories();
      if (!result.success) return result;
      
      const categories = result.data.filter(c => c.type === type);
      return {
        success: true,
        data: categories,
        total: categories.length
      };
    } catch (e) {
      const categories = this.categories.filter(c => c.type === type);
      return {
        success: true,
        data: categories,
        total: categories.length,
        fallback: true
      };
    }
  }

  // Kategorien nach Altersgruppe abrufen (verwendet lokale Filterung)
  async getCategoriesByAgeGroup(ageGroup) {
    try {
      const result = await this.getAllCategories();
      if (!result.success) return result;
      
      const categories = result.data.filter(c => 
        c.ageGroups && c.ageGroups.includes(ageGroup)
      );
      return {
        success: true,
        data: categories,
        total: categories.length
      };
    } catch (e) {
      const categories = this.categories.filter(c => 
        c.ageGroups && c.ageGroups.includes(ageGroup)
      );
      return {
        success: true,
        data: categories,
        total: categories.length,
        fallback: true
      };
    }
  }

  // Kategorien nach Geschlecht abrufen (verwendet lokale Filterung)
  async getCategoriesByGender(gender) {
    try {
      const result = await this.getAllCategories();
      if (!result.success) return result;
      
      const categories = result.data.filter(c => 
        c.gender === gender || c.gender === 'mixed'
      );
      return {
        success: true,
        data: categories,
        total: categories.length
      };
    } catch (e) {
      const categories = this.categories.filter(c => 
        c.gender === gender || c.gender === 'mixed'
      );
      return {
        success: true,
        data: categories,
        total: categories.length,
        fallback: true
      };
    }
  }

  // Neue Funktion: Kategorien synchron nach Altersgruppe filtern
  getCategoriesByAgeGroupSync(ageGroup) {
    const categories = this.categories.filter(c => 
      c.ageGroups && c.ageGroups.includes(ageGroup)
    );
    return categories;
  }

  // Neue Funktion: Kategorien nach Typ und Altersgruppe filtern
  getCategoriesByTypeAndAgeGroupSync(type, ageGroup) {
    const categories = this.categories.filter(c => 
      c.type === type && c.ageGroups && c.ageGroups.includes(ageGroup)
    );
    return categories;
  }
}

// Singleton-Instanz exportieren
const categoriesService = new CategoriesService();
export default categoriesService; 